import Phaser from 'phaser';
import { FACTIONS, PLAYER_CLASSES } from '../data/GameConstants';

export default class FactionSelectScene extends Phaser.Scene {
  constructor() {
    super({ key: 'FactionSelectScene' });
    this.selectedFaction = null;
    this.selectedClass = null;
  }

  create() {
    const { width, height } = this.cameras.main;
    this.cameras.main.setBackgroundColor('#0A0505');

    // Title
    this.add.text(width / 2, 50, 'CHOOSE YOUR FACTION', {
      font: 'bold 36px Playfair Display',
      fill: '#FFD700'
    }).setOrigin(0.5);

    // Faction cards
    const factionKeys = Object.keys(FACTIONS);
    const cardWidth = 200;
    const cardHeight = 280;
    const startX = (width - (factionKeys.length * cardWidth + (factionKeys.length - 1) * 20)) / 2;

    this.factionCards = [];

    factionKeys.forEach((key, index) => {
      const faction = FACTIONS[key];
      const x = startX + index * (cardWidth + 20) + cardWidth / 2;
      const y = height * 0.4;

      const card = this.createFactionCard(x, y, cardWidth, cardHeight, faction, key);
      this.factionCards.push(card);
    });

    // Class selection (hidden until faction selected)
    this.classContainer = this.add.container(width / 2, height * 0.75);
    this.classContainer.setVisible(false);

    this.add.text(width / 2, height * 0.65, 'SELECT CLASS', {
      font: '24px Playfair Display',
      fill: '#FFFFFF'
    }).setOrigin(0.5);

    this.createClassSelection();

    // Start button (hidden until selections made)
    this.startButton = this.add.text(width / 2, height - 60, 'BEGIN JOURNEY', {
      font: 'bold 28px Manrope',
      fill: '#333333',
      backgroundColor: '#666666',
      padding: { x: 30, y: 15 }
    }).setOrigin(0.5);

    // Back button
    this.add.text(50, height - 40, '← BACK', {
      font: '20px Manrope',
      fill: '#FFFFFF'
    }).setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.scene.start('TitleScene'));

    // Fade in
    this.cameras.main.fadeIn(500, 0, 0, 0);
  }

  createFactionCard(x, y, width, height, faction, key) {
    const container = this.add.container(x, y);

    // Card background
    const bg = this.add.rectangle(0, 0, width, height, 0x1A1A1A)
      .setStrokeStyle(2, Phaser.Display.Color.HexStringToColor(faction.color).color);

    // Faction icon placeholder (colored circle)
    const iconColor = Phaser.Display.Color.HexStringToColor(faction.color).color;
    const icon = this.add.circle(0, -80, 40, iconColor);

    // Faction name
    const name = this.add.text(0, -20, faction.name.toUpperCase(), {
      font: 'bold 18px Manrope',
      fill: faction.color
    }).setOrigin(0.5);

    // Description
    const desc = this.add.text(0, 30, faction.description, {
      font: '12px Manrope',
      fill: '#AAAAAA',
      wordWrap: { width: width - 20 },
      align: 'center'
    }).setOrigin(0.5);

    // Weakness
    const weakness = this.add.text(0, 100, `⚠ ${faction.weakness}`, {
      font: '11px Manrope',
      fill: '#FF6B6B'
    }).setOrigin(0.5);

    container.add([bg, icon, name, desc, weakness]);

    // Make interactive
    bg.setInteractive({ useHandCursor: true });

    bg.on('pointerover', () => {
      bg.setStrokeStyle(3, 0xFFD700);
      container.setScale(1.05);
    });

    bg.on('pointerout', () => {
      if (this.selectedFaction !== key) {
        bg.setStrokeStyle(2, iconColor);
        container.setScale(1);
      }
    });

    bg.on('pointerdown', () => {
      this.selectFaction(key, container, bg, iconColor);
    });

    container.bg = bg;
    container.iconColor = iconColor;
    container.factionKey = key;

    return container;
  }

  selectFaction(key, container, bg, iconColor) {
    // Deselect previous
    this.factionCards.forEach(card => {
      card.setScale(1);
      card.bg.setStrokeStyle(2, card.iconColor);
    });

    // Select new
    this.selectedFaction = key;
    container.setScale(1.05);
    bg.setStrokeStyle(3, 0xFFD700);

    // Show class selection
    this.classContainer.setVisible(true);
    this.updateStartButton();
  }

  createClassSelection() {
    const classKeys = Object.keys(PLAYER_CLASSES);
    const buttonWidth = 120;
    const startX = -(classKeys.length * buttonWidth + (classKeys.length - 1) * 10) / 2;

    this.classButtons = [];

    classKeys.forEach((key, index) => {
      const playerClass = PLAYER_CLASSES[key];
      const x = startX + index * (buttonWidth + 10) + buttonWidth / 2;

      const button = this.add.container(x, 0);

      const bg = this.add.rectangle(0, 0, buttonWidth, 60, 0x333333)
        .setStrokeStyle(1, 0x666666);

      const text = this.add.text(0, 0, playerClass.name, {
        font: '16px Manrope',
        fill: '#FFFFFF'
      }).setOrigin(0.5);

      button.add([bg, text]);
      this.classContainer.add(button);

      bg.setInteractive({ useHandCursor: true });

      bg.on('pointerover', () => {
        if (this.selectedClass !== key) {
          bg.setFillStyle(0x444444);
        }
      });

      bg.on('pointerout', () => {
        if (this.selectedClass !== key) {
          bg.setFillStyle(0x333333);
        }
      });

      bg.on('pointerdown', () => {
        this.selectClass(key, bg);
      });

      button.bg = bg;
      button.classKey = key;
      this.classButtons.push(button);
    });
  }

  selectClass(key, bg) {
    // Deselect previous
    this.classButtons.forEach(button => {
      button.bg.setFillStyle(0x333333);
      button.bg.setStrokeStyle(1, 0x666666);
    });

    // Select new
    this.selectedClass = key;
    bg.setFillStyle(0x4CAF50);
    bg.setStrokeStyle(2, 0xFFD700);

    this.updateStartButton();
  }

  updateStartButton() {
    if (this.selectedFaction && this.selectedClass) {
      this.startButton.setStyle({
        backgroundColor: '#FFD700',
        fill: '#000000'
      });

      this.startButton.setInteractive({ useHandCursor: true });
      
      this.startButton.off('pointerdown');
      this.startButton.on('pointerdown', () => {
        // Store selections in registry
        this.registry.set('playerFaction', this.selectedFaction);
        this.registry.set('playerClass', this.selectedClass);
        
        this.cameras.main.fadeOut(500, 0, 0, 0);
        this.time.delayedCall(500, () => {
          this.scene.start('GameScene');
        });
      });
    }
  }
}
